import { Node } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { Plugin } from '@tiptap/pm/state'
import { reset } from 'linkifyjs'

import { PreviewComponent } from './PreviewComponent'
import { pasteHandler } from './utils/pasteHandler'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    'embed-preview': {
      /**
       * Set a footnote
       */
      setEmbedPreview: (options: {
        src: string
        url: string
        title: string
        loading?: boolean
      }) => ReturnType
    }
  }
}

export interface EmbedPreviewOptions {
  HTMLAttributes: Record<string, any>
}

export const URL_REGEX =
  /[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/

export const Preview = Node.create<EmbedPreviewOptions>({
  name: 'embed-preview',
  group: 'block',
  content: 'inline*',
  atom: true,
  draggable: true,
  isolating: true,
  onDestroy() {
    reset()
  },
  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      url: {
        default: null,
        parseHTML: (element) => {
          return element?.getAttribute('href')
        },
        renderHTML: (attributes) => {
          return {
            url: attributes.url,
          }
        },
      },
      src: {
        default: null,
        parseHTML: (element) => {
          return element?.querySelector('img')?.getAttribute('src')
        },
      },
      title: {
        default: null,
        parseHTML: (element) => {
          return element?.querySelector('span')?.textContent
        },
        renderHTML: (attributes) => {
          return {
            title: attributes.title,
          }
        },
      },
    }
  },
  parseHTML() {
    return [
      {
        tag: 'embed-preview',
        contentElement: 'span',
      },
    ]
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'embed-preview',
      {
        href: HTMLAttributes.url,
        contenteditable: false,
      },
      [
        'img',
        {
          src: HTMLAttributes.src,
        },
      ],
      ['span', HTMLAttributes.title],
    ]
  },
  addNodeView() {
    return ReactNodeViewRenderer(PreviewComponent)
  },
  addCommands() {
    return {
      setEmbedPreview:
        ({ url, src, title }) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: { url, src },
              content: [{ type: 'text', text: title }],
            })
            .run()
        },
    }
  },
  addProseMirrorPlugins() {
    const plugins: Plugin[] = []

    plugins.push(
      pasteHandler({
        editor: this.editor,
        type: this.type,
        name: this.name,
      })
    )

    return plugins
  },
})
